import React, { useEffect } from "react";
import { PropTypes } from "prop-types";
import { useTheme } from "@mui/material/styles";
import { Box, Divider, CircularProgress } from "@mui/material";
import FullPageLayout from "../FullPageLayout";
import { useDispatch, useSelector } from "react-redux";
import { fetchData } from "../../redux/actions/learningCenter/activityActions";
import {
  buildCoursePlayerRoute,
  buildContentViewerRoute
} from "../../routes/LearningCenterRoutes";
import { push } from "connected-react-router";
import CourseOverviewPage from "./CourseOverviewPage";
import { useCourse } from "../../hooks/learningCenter/myCourses";
import { makeGetEntities } from "../../redux/selectors/selectors";
import { fetchCourseCtoa } from "../../redux/actions/userAssignmentsListActions";
import { courseSectionTypes } from "../../constants/index";
import useLoadingState from "../../hooks/useLoadingState";

const selector = makeGetEntities();

const { filter } = require("lodash");

const CourseOverviewContainer = ({
  match: {
    params: {
      assignedTrainingObjectId,
      versionId,
      virtualClassroomParentSession
    }
  },
  fullPageLayout
}) => {
  const dispatch = useDispatch();
  const { fetchCourse } = useCourse();
  const theme = useTheme();
  const loading = useLoadingState([
    "FETCH_USER_ASSIGNMENTS_LIST",
    "FETCH_COURSE_CTOA"
  ]);
  const { version, assignment } = useSelector(
    ({ userAssignmentsListState }) => {
      const version =
        selector(userAssignmentsListState, {
          type: "version",
          id: versionId
        }) || {};
      return {
        version,
        assignment:
          selector(userAssignmentsListState, {
            type: "customerTrainingObjectAssignment"
          })?.find(assignment => assignment.id == version?.majorReleaseId) || {}
      };
    }
  );

  useEffect(() => {
    fetchCourse(versionId);
    dispatch(
      fetchCourseCtoa(
        virtualClassroomParentSession
          ? virtualClassroomParentSession
          : assignedTrainingObjectId
      )
    );
  }, []);

  useEffect(() => {
    if (
      assignment?.assignmentId &&
      version?.showOverview &&
      assignment?.overviewActivityId
    ) {
      dispatch(
        fetchData(assignment.assignmentId, assignment.overviewActivityId)
      );
    }
  }, [assignment.assignmentId, version.id]);
  /**
   * When clicked, takes user to the table of contents for the course
   */
  const handleViewOutlineClick = () => {
    dispatch(
      push(
        buildCoursePlayerRoute(
          virtualClassroomParentSession
            ? virtualClassroomParentSession
            : assignedTrainingObjectId
        )
      )
    );
  };

  /**
   * When clicked, takes user to the content viewer for the first course activity
   */
  const handleGetStartedClick = () => {
    if (assignment?.id) {
      const firstShortCourse =
        assignment.courseSections &&
        filter(assignment.courseSections, {
          isOverview: false,
          sectionType: courseSectionTypes.mandatory
        })[0];
      const firstActivity =
        firstShortCourse?.courseContentables &&
        firstShortCourse.courseContentables[0];
      if (firstActivity) {
        dispatch(
          push(
            buildContentViewerRoute(assignment.assignmentId, firstActivity.id)
          )
        );
      } else {
        handleViewOutlineClick();
      }
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        flex={1}
        height={`calc(100vh - ${theme.sizes.topNavBarHeight}px)`}
      >
        <CircularProgress size={80} />
      </Box>
    );
  }

  if (version?.id) {
    const {
      name,
      description,
      typeName,
      duration,
      image,
      passingScore,
      overviewText,
      joinUrl,
      passFail
    } = version;

    if (fullPageLayout === false) {
      return (
        <CourseOverviewPage
          overviewText={overviewText}
          description={description}
          courseType={typeName}
          duration={duration}
          image={image}
          passingScore={passingScore}
          onViewOutlineClick={handleViewOutlineClick}
          onGetStartedClick={handleGetStartedClick}
          joinUrl={joinUrl}
          passFail={passFail}
        />
      );
    } else {
      return (
        <FullPageLayout title={name}>
          <Divider sx={{ mb: 5 }} />
          <CourseOverviewPage
            overviewText={overviewText}
            description={description}
            courseType={typeName}
            duration={duration}
            image={image}
            passingScore={passingScore}
            onViewOutlineClick={handleViewOutlineClick}
            onGetStartedClick={handleGetStartedClick}
            joinUrl={joinUrl}
            passFail={passFail}
          />
        </FullPageLayout>
      );
    }
  }

  return null;
};

export default CourseOverviewContainer;

CourseOverviewContainer.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      versionId: PropTypes.string,
      assignedTrainingObjectId: PropTypes.string
    })
  })
};
